import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, CheckCircle, XCircle, Receipt, TrendingUp, Printer, Calendar, Filter, Gamepad2, ShoppingCart, ChevronDown, CreditCard, Wallet } from 'lucide-react';
import PaymentDialog from './PaymentDialog';
import PrintDialog from './PrintDialog';
import FnBPrintDialog from './FnBPrintDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Modern Custom Dropdown Component
function ModernSelect({ value, onChange, options, icon: Icon, label }) {
  return (
    <div className="relative">
      <label className="text-white text-xs font-semibold mb-2 block flex items-center gap-1.5">
        {Icon && <Icon className="h-3.5 w-3.5 text-blue-400" />}
        {label}
      </label>
      <div className="relative">
        <select
          value={value}
          onChange={onChange}
          className="w-full pl-4 pr-10 py-2.5 bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-2 border-slate-600/50 rounded-xl text-white text-sm font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 hover:border-slate-500 transition-all appearance-none cursor-pointer shadow-lg backdrop-blur-sm"
        >
          {options.map(opt => (
            <option key={opt.value} value={opt.value} className="bg-slate-800 text-white">
              {opt.label}
            </option>
          ))}
        </select>
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
          <ChevronDown className="h-4 w-4 text-blue-400" />
        </div>
      </div>
    </div>
  );
}

export default function RentalHistory() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRental, setSelectedRental] = useState(null);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [stats, setStats] = useState({ 
    total: 0, 
    paid: 0, 
    unpaid: 0, 
    revenue: 0,
    psRevenue: 0,
    fnbRevenue: 0,
    qrisRevenue: 0,
    cashRevenue: 0
  });
  
  // Print Dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [showFnBPrintDialog, setShowFnBPrintDialog] = useState(false);
  const [selectedPrintRental, setSelectedPrintRental] = useState(null);
  const [selectedPrintFnB, setSelectedPrintFnB] = useState(null);
  
  // Filters - DEFAULT TO TODAY
  const [dateFilter, setDateFilter] = useState('today');
  const [typeFilter, setTypeFilter] = useState('all');
  const [cashierFilter, setCashierFilter] = useState('all');
  const [branchFilter, setBranchFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  const [showCustomDate, setShowCustomDate] = useState(false);
  
  // Available cashiers from database (for dropdown)
  const [availableCashiers, setAvailableCashiers] = useState([]);
  const [availableBranches, setAvailableBranches] = useState([]);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;
  
  // Expanded rows state
  const [expandedRows, setExpandedRows] = useState(new Set());

  // Fetch available cashiers and branches on component mount
  useEffect(() => {
    fetchAvailableCashiers();
    fetchAvailableBranches();
  }, []);

  const fetchAvailableCashiers = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.get(`${API}/users`, { headers });
      const users = response.data.users || [];
      
      // Get unique cashier usernames (role = kasir)
      const cashiers = users
        .filter(user => user.role === 'kasir')
        .map(user => user.username)
        .filter(Boolean);
      
      setAvailableCashiers([...new Set(cashiers)]);
    } catch (error) {
      console.error('Error fetching cashiers:', error);
      // Fallback: use cashiers from current transactions
      const fallbackCashiers = [...new Set(transactions.map(t => t.operator_name).filter(Boolean))];
      setAvailableCashiers(fallbackCashiers);
    }
  };

  const fetchAvailableBranches = async () => {
    try {
      const token = localStorage.getItem('token');
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // For kasir: Use their branch only
      // For admin/supervisor: Fetch from backend business config or use smart approach
      if (user.role === 'kasir') {
        // Kasir only has access to their own branch
        const userBranch = user.branch || user.branch_name;
        if (userBranch) {
          setAvailableBranches([userBranch]);
          console.log('[RentalHistory] Kasir branch:', userBranch);
        }
      } else {
        // Admin/Supervisor: use the dedicated lightweight branch endpoint.
        try {
          const response = await axios.get(`${API}/branches`, { headers });
          const branches = response.data.branches || response.data || [];
          setAvailableBranches(branches.map(branch => branch.name).filter(Boolean));
        } catch (err) {
          console.error('Error fetching branches:', err);
        }
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  useEffect(() => {
    // OPTIMIZED: Clear transactions on filter change for fresh data
    setTransactions([]);
    setLoading(true);
    
    fetchHistory();
    
    // OPTIMIZED: Increase auto-refresh to 30 seconds to reduce server load
    // User can manually refresh if needed
    const interval = setInterval(() => {
      fetchHistory();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [dateFilter, typeFilter, customDateRange, branchFilter, cashierFilter, paymentStatusFilter]);

  const fetchHistory = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      // CRITICAL: Add timestamp to prevent caching
      const timestamp = Date.now();
      
      let params = {
        date_filter: dateFilter,
        type_filter: typeFilter,
        limit: 200,  // OPTIMIZED: Increased to 200 for better coverage
        _t: timestamp  // Cache buster
      };
      
      // OPTIMIZED: Send payment status filter to backend
      if (paymentStatusFilter !== 'all') {
        params.payment_status_filter = paymentStatusFilter;
      }
      
      // CRITICAL FIX: Send branch_id to backend for server-side filtering
      if (branchFilter !== 'all') {
        params.branch_id = branchFilter;
        console.log('[RentalHistory] Fetching transactions for branch:', branchFilter);
      }
      
      // CRITICAL FIX: If kasir filter selected, send cashier_username to backend
      // Backend will fetch kasir's active shift and use that time window
      if (cashierFilter !== 'all') {
        params.cashier_username = cashierFilter;
        console.log('[RentalHistory] Fetching transactions for kasir:', cashierFilter);
      }
      
      if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
        // FIXED: Send date in YYYY-MM-DD format, backend will interpret as Jakarta timezone
        params.start_date = customDateRange.start;  // Already in YYYY-MM-DD format from date input
        params.end_date = customDateRange.end;      // Already in YYYY-MM-DD format from date input
        console.log('[RentalHistory] Custom date range:', params.start_date, 'to', params.end_date);
      }
      
      const response = await axios.get(`${API}/transactions/unified`, { 
        headers,
        params
      });
      
      const data = response.data;
      const transactionsData = data.transactions || [];
      const summary = data.summary || {};
      
      // CRITICAL: Log for debugging filter issues
      console.log('[RentalHistory] ===== API RESPONSE =====');
      console.log('[RentalHistory] Filter:', dateFilter, typeFilter);
      console.log('[RentalHistory] Received:', transactionsData.length, 'transactions');
      if (transactionsData.length > 0) {
        const firstTrans = transactionsData[0];
        const lastTrans = transactionsData[transactionsData.length - 1];
        console.log('[RentalHistory] First transaction:', firstTrans.actual_end_time || firstTrans.paid_at);
        console.log('[RentalHistory] Last transaction:', lastTrans.actual_end_time || lastTrans.paid_at);
      }
      
      // Calculate stats first
      const paid = transactionsData.filter(t => {
        if (t.transaction_type === 'ps') {
          return t.payment_status === 'paid';
        } else {
          return t.payment_status === 'paid';
        }
      }).length;
      
      const unpaid = transactionsData.filter(t => {
        if (t.transaction_type === 'ps') {
          return t.payment_status === 'unpaid';
        } else {
          return t.payment_status === 'unpaid';
        }
      }).length;
      
      const newStats = {
        total: transactionsData.length,
        paid,
        unpaid,
        revenue: summary.total_revenue || 0,
        psRevenue: summary.ps_revenue || 0,
        fnbRevenue: summary.fnb_revenue || 0,
        qrisRevenue: summary.qris_revenue || 0,
        cashRevenue: summary.cash_revenue || 0
      };
      
      // CRITICAL FIX: Batch state updates to prevent flickering
      // Use React 18 automatic batching or setTimeout to batch updates
      // This ensures cards only render ONCE with correct data
      setTransactions(transactionsData);
      setStats(newStats);
    } catch (error) {
      console.error('Error fetching transaction history:', error);
      toast.error('Gagal memuat riwayat transaksi');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentClick = (rental) => {
    setSelectedRental(rental);
    setShowPaymentDialog(true);
  };

  const handlePaymentSuccess = async (rentalId, paymentMethod, amountReceived, newItems = []) => {
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};
      
      const response = await axios.post(
        `${API}/rentals/${rentalId}/payment`,
        { 
          payment_method: paymentMethod, 
          amount_received: amountReceived,
          additional_items: newItems
        },
        { headers }
      );
      
      // Fetch rental with updated payment info from API
      const rentalResponse = await axios.get(`${API}/rentals/${rentalId}`, { headers });
      const updatedRental = rentalResponse.data;
      
      // Add payment details to rental object for printing
      updatedRental.payment_method = paymentMethod;
      updatedRental.amount_received = amountReceived;
      updatedRental.change = paymentMethod === 'cash' ? amountReceived - (updatedRental.final_price || 0) : 0;
      
      // Refresh history list
      await fetchHistory();
      
      // Show print dialog with complete rental data
      setSelectedPrintRental(updatedRental);
      setShowPrintDialog(true);
      toast.success('Pembayaran berhasil! Silakan cetak struk.');
    } catch (error) {
      console.error(' Payment error:', error);
      throw error;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('id-ID', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'Asia/Jakarta' // GMT+7
    });
  };

  // OPTIMIZED: Backend now handles most filtering
  // Only need to do frontend filtering for branch (kept for backwards compatibility)
  let filteredTransactions = transactions;
  
  // CRITICAL: Backend now handles:
  // - Date filter
  // - Cashier filter (via shift lookup)
  // - Payment status filter (NEW - optimized)
  // 
  // Frontend only handles:
  // - Branch filter (for kasir/supervisor who can't change branch anyway)
  
  if (branchFilter !== 'all') {
    filteredTransactions = filteredTransactions.filter(t => t.branch === branchFilter);
  }
  
  // Get unique branches for filter dropdown
  // Use availableBranches from state instead of deriving from filtered transactions
  // This prevents circular dependency where empty transactions = empty branch dropdown
  const uniqueBranches = availableBranches;
  
  // Calculate stats from filtered transactions
  // If cashier or branch filter is active, recalculate stats from filtered transactions
  // Otherwise, use stats from backend (which already includes date filter)
  const filteredStats = (cashierFilter !== 'all' || branchFilter !== 'all') ? {
    total: filteredTransactions.length,
    paid: filteredTransactions.filter(t => t.payment_status === 'paid').length,
    unpaid: filteredTransactions.filter(t => t.payment_status === 'unpaid').length,
    revenue: filteredTransactions.reduce((sum, t) => {
      // Only count PAID transactions
      if (t.payment_status !== 'paid') return sum;
      
      if (t.transaction_type === 'ps') {
        return sum + (t.final_price || t.total_price || 0);
      } else {
        return sum + (t.total || 0);
      }
    }, 0),
    psRevenue: filteredTransactions.reduce((sum, t) => {
      if (t.transaction_type === 'ps' && t.payment_status === 'paid') {
        const finalPrice = t.final_price || t.total_price || 0;
        // Calculate F&B from additional items
        const fnbFromItems = (t.additional_items || []).reduce((itemSum, item) => 
          itemSum + ((item.price || 0) * (item.qty || 1)), 0);
        // PS revenue only = final_price - F&B items
        return sum + (finalPrice - fnbFromItems);
      }
      return sum;
    }, 0),
    fnbRevenue: filteredTransactions.reduce((sum, t) => {
      if (t.payment_status === 'paid') {
        if (t.transaction_type === 'fnb') {
          // Standalone F&B order
          return sum + (t.total || 0);
        } else if (t.transaction_type === 'ps') {
          // F&B from PS rental additional items
          const fnbFromItems = (t.additional_items || []).reduce((itemSum, item) => 
            itemSum + ((item.price || 0) * (item.qty || 1)), 0);
          return sum + fnbFromItems;
        }
      }
      return sum;
    }, 0),
    qrisRevenue: filteredTransactions.reduce((sum, t) => {
      if (t.payment_status === 'paid' && t.payment_method === 'qris') {
        if (t.transaction_type === 'ps') {
          return sum + (t.final_price || t.total_price || 0);
        } else {
          return sum + (t.total || 0);
        }
      }
      return sum;
    }, 0),
    cashRevenue: filteredTransactions.reduce((sum, t) => {
      if (t.payment_status === 'paid' && t.payment_method === 'cash') {
        if (t.transaction_type === 'ps') {
          return sum + (t.final_price || t.total_price || 0);
        } else {
          return sum + (t.total || 0);
        }
      }
      return sum;
    }, 0)
  } : stats;
  
  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedTransactions = filteredTransactions.slice(startIndex, endIndex);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const onPrintClick = (transaction) => {
    if (transaction.transaction_type === 'ps') {
      setSelectedPrintRental(transaction);
      setShowPrintDialog(true);
    } else {
      setSelectedPrintFnB(transaction);
      setShowFnBPrintDialog(true);
    }
  };
  
  const toggleRowExpand = (transactionId) => {
    const newExpanded = new Set(expandedRows);
    if (newExpanded.has(transactionId)) {
      newExpanded.delete(transactionId);
    } else {
      newExpanded.add(transactionId);
    }
    setExpandedRows(newExpanded);
  };

  const getDateFilterLabel = (filter) => {
    const labels = {
      'all': 'Semua',
      'today': 'Hari Ini',
      'yesterday': 'Kemarin',
      'this_month': 'Bulan Ini',
      'last_month': 'Bulan Kemarin',
      '3_months': '3 Bulan Terakhir',
      '6_months': '6 Bulan Terakhir',
      'custom': 'Custom'
    };
    return labels[filter] || filter;
  };

  const getTypeFilterLabel = (filter) => {
    const labels = {
      'all': 'Semua Transaksi',
      'ps': 'PS Rental Saja',
      'fnb': 'F&B Saja'
    };
    return labels[filter] || filter;
  };

  // REMOVED: Full screen loading that causes scroll jump
  // Now we'll show loading indicator inline without replacing content

  const dateFilterOptions = [
    { value: 'all', label: 'Semua Waktu' },
    { value: 'today', label: 'Hari Ini' },
    { value: 'yesterday', label: 'Kemarin' },
    { value: 'this_month', label: 'Bulan Ini' },
    { value: 'last_month', label: 'Bulan Kemarin' },
    { value: '3_months', label: '3 Bulan Terakhir' },
    { value: '6_months', label: '6 Bulan Terakhir' },
    { value: 'custom', label: 'Pilih Tanggal' }
  ];

  const typeFilterOptions = [
    { value: 'all', label: ' Semua Transaksi' },
    { value: 'ps', label: ' PS Rental Saja' },
    { value: 'fnb', label: ' F&B Saja' }
  ];

  return (
    <div className="space-y-6">
      {/* Filter Section */}
      <div className="bg-gradient-to-br from-slate-800/95 via-slate-800/90 to-slate-900/95 border-2 border-slate-700/70 rounded-2xl p-5 shadow-2xl backdrop-blur-sm">
        <h3 className="text-base font-bold text-white mb-4 flex items-center gap-2">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Filter className="h-4 w-4 text-blue-400" />
          </div>
          <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Filter Transaksi</span>
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Date Filter */}
          <ModernSelect
            value={dateFilter}
            onChange={(e) => {
              setDateFilter(e.target.value);
              setShowCustomDate(e.target.value === 'custom');
              setCurrentPage(1);
            }}
            options={dateFilterOptions}
            icon={Calendar}
            label="Periode Waktu"
          />

          {/* Type Filter */}
          <ModernSelect
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={typeFilterOptions}
            icon={Filter}
            label="Tipe Transaksi"
          />

          {/* Cashier Filter */}
          <ModernSelect
            value={cashierFilter}
            onChange={(e) => {
              setCashierFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: 'all', label: ' Semua Kasir' },
              ...availableCashiers.map(cashier => ({ value: cashier, label: cashier }))
            ]}
            icon={Filter}
            label="Kasir"
          />

          {/* Branch Filter */}
          <ModernSelect
            value={branchFilter}
            onChange={(e) => {
              setBranchFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: 'all', label: ' Semua Cabang' },
              ...uniqueBranches.map(branch => ({ value: branch, label: branch }))
            ]}
            icon={Filter}
            label="Cabang"
          />
          
          {/* Payment Status Filter */}
          <ModernSelect
            value={paymentStatusFilter}
            onChange={(e) => {
              setPaymentStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
            options={[
              { value: 'all', label: ' Semua Status' },
              { value: 'paid', label: ' Sudah Dibayar' },
              { value: 'unpaid', label: ' Belum Dibayar' }
            ]}
            icon={CheckCircle}
            label="Status Pembayaran"
          />
        </div>

        {/* Custom Date Range */}
        {showCustomDate && (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-2 duration-300">
            <div>
              <label className="text-white text-xs font-semibold mb-2 block"> Dari Tanggal</label>
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) => setCustomDateRange({ ...customDateRange, start: e.target.value })}
                className="w-full px-4 py-2.5 bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-2 border-slate-600/50 rounded-xl text-white text-sm font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all shadow-lg"
              />
            </div>
            <div>
              <label className="text-white text-xs font-semibold mb-2 block"> Sampai Tanggal</label>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) => setCustomDateRange({ ...customDateRange, end: e.target.value })}
                className="w-full px-4 py-2.5 bg-gradient-to-br from-slate-900/90 to-slate-800/90 border-2 border-slate-600/50 rounded-xl text-white text-sm font-medium focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all shadow-lg"
              />
            </div>
          </div>
        )}
      </div>

      {/* Stats Cards - More Modern Icons */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <div className="bg-gradient-to-br from-blue-500/80 via-blue-600/70 to-blue-700/80 border-2 border-blue-400/50 rounded-2xl p-3 text-white shadow-xl hover:scale-105 hover:shadow-blue-500/50 transition-all relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <div className="relative flex flex-col items-center justify-center text-center">
            <div className="p-2 bg-blue-400/30 rounded-xl backdrop-blur-sm mb-2">
              <Receipt className="h-5 w-5 text-white drop-shadow-lg" />
            </div>
            <p className="text-blue-100 text-[10px] font-bold uppercase tracking-wider">Total</p>
            <p className="text-2xl font-black mt-0.5 text-white">{filteredStats.total}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-purple-500/80 via-purple-600/70 to-purple-700/80 border-2 border-purple-400/50 rounded-2xl p-3 text-white shadow-xl hover:scale-105 hover:shadow-purple-500/50 transition-all relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <div className="relative flex flex-col items-center justify-center text-center">
            <div className="p-2 bg-purple-400/30 rounded-xl backdrop-blur-sm mb-2">
              <TrendingUp className="h-5 w-5 text-white drop-shadow-lg" />
            </div>
            <p className="text-purple-100 text-[10px] font-bold uppercase tracking-wider">Total</p>
            <p className="text-sm font-black mt-0.5 text-white leading-tight break-all">{formatCurrency(filteredStats.revenue)}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-green-500/80 via-green-600/70 to-green-700/80 border-2 border-green-400/50 rounded-2xl p-3 text-white shadow-xl hover:scale-105 hover:shadow-green-500/50 transition-all relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <div className="relative flex flex-col items-center justify-center text-center">
            <div className="p-2 bg-green-400/30 rounded-xl backdrop-blur-sm mb-2">
              <Gamepad2 className="h-5 w-5 text-white drop-shadow-lg" />
            </div>
            <p className="text-green-100 text-[10px] font-bold uppercase tracking-wider">PS</p>
            <p className="text-sm font-black mt-0.5 text-white leading-tight break-all">{formatCurrency(filteredStats.psRevenue)}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-orange-500/80 via-orange-600/70 to-orange-700/80 border-2 border-orange-400/50 rounded-2xl p-3 text-white shadow-xl hover:scale-105 hover:shadow-orange-500/50 transition-all relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <div className="relative flex flex-col items-center justify-center text-center">
            <div className="p-2 bg-orange-400/30 rounded-xl backdrop-blur-sm mb-2">
              <ShoppingCart className="h-5 w-5 text-white drop-shadow-lg" />
            </div>
            <p className="text-orange-100 text-[10px] font-bold uppercase tracking-wider">F&B</p>
            <p className="text-sm font-black mt-0.5 text-white leading-tight break-all">{formatCurrency(filteredStats.fnbRevenue)}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-cyan-500/80 via-cyan-600/70 to-cyan-700/80 border-2 border-cyan-400/50 rounded-2xl p-3 text-white shadow-xl hover:scale-105 hover:shadow-cyan-500/50 transition-all relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <div className="relative flex flex-col items-center justify-center text-center">
            <div className="p-2 bg-cyan-400/30 rounded-xl backdrop-blur-sm mb-2">
              <CreditCard className="h-5 w-5 text-white drop-shadow-lg" />
            </div>
            <p className="text-cyan-100 text-[10px] font-bold uppercase tracking-wider">QRIS</p>
            <p className="text-sm font-black mt-0.5 text-white leading-tight break-all">{formatCurrency(filteredStats.qrisRevenue)}</p>
          </div>
        </div>

        <div className="bg-gradient-to-br from-emerald-500/80 via-emerald-600/70 to-emerald-700/80 border-2 border-emerald-400/50 rounded-2xl p-3 text-white shadow-xl hover:scale-105 hover:shadow-emerald-500/50 transition-all relative overflow-hidden group">
          <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/10 to-white/0 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
          <div className="relative flex flex-col items-center justify-center text-center">
            <div className="p-2 bg-emerald-400/30 rounded-xl backdrop-blur-sm mb-2">
              <Wallet className="h-5 w-5 text-white drop-shadow-lg" />
            </div>
            <p className="text-emerald-100 text-[10px] font-bold uppercase tracking-wider">Cash</p>
            <p className="text-sm font-black mt-0.5 text-white leading-tight break-all">{formatCurrency(filteredStats.cashRevenue)}</p>
          </div>
        </div>
      </div>

      {/* Table View */}
      <div className="bg-gradient-to-br from-slate-800/95 via-slate-800/90 to-slate-900/95 border-2 border-slate-700/70 rounded-2xl shadow-2xl overflow-hidden backdrop-blur-sm">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600 px-5 py-4 border-b border-slate-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <Receipt className="h-5 w-5 text-white" />
              </div>
              <div>
                <h3 className="text-lg font-black text-white">Daftar Transaksi</h3>
                <p className="text-xs text-blue-200 mt-0.5">
                  {getDateFilterLabel(dateFilter)} • {getTypeFilterLabel(typeFilter)}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {loading && (
                <div className="flex items-center gap-2 text-xs text-blue-200 bg-blue-500/20 px-3 py-1.5 rounded-full font-semibold backdrop-blur-sm animate-pulse">
                  <div className="animate-spin rounded-full h-3 w-3 border-2 border-blue-300 border-t-transparent"></div>
                  Memuat...
                </div>
              )}
              <div className="text-xs text-blue-200 bg-white/10 px-3 py-1.5 rounded-full font-semibold backdrop-blur-sm">
                {filteredTransactions.length} transaksi
              </div>
            </div>
          </div>
        </div>

        {paginatedTransactions.length === 0 ? (
          <div className="text-center py-16">
            <Clock className="mx-auto h-16 w-16 text-white/30 mb-4" />
            <p className="text-white/60 text-lg">Belum ada riwayat transaksi</p>
          </div>
        ) : (
          <>
            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-900/50 sticky top-0">
                  <tr className="border-b-2 border-slate-700">
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">No</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">Waktu</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">Customer</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">Unit</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">Mode</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">Tipe</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">Metode</th>
                    <th className="px-4 py-3 text-left text-xs font-bold text-blue-300 uppercase tracking-wider">Kasir</th>
                    <th className="px-4 py-3 text-right text-xs font-bold text-blue-300 uppercase tracking-wider">Total</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-blue-300 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-center text-xs font-bold text-blue-300 uppercase tracking-wider">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTransactions.map((transaction, index) => {
                    const isPS = transaction.transaction_type === 'ps';
                    const isFnB = transaction.transaction_type === 'fnb';
                    const isPaid = transaction.payment_status === 'paid';
                    const rowNumber = startIndex + index + 1;
                    const hasAdditionalItems = isPS && transaction.additional_items && transaction.additional_items.length > 0;
                    const hasFnBItems = isFnB && transaction.items && transaction.items.length > 0;
                    const isExpanded = expandedRows.has(transaction.id);
                    const showExpandable = hasAdditionalItems || hasFnBItems;
                    
                    return (
                      <React.Fragment key={transaction.id}>
                        <tr
                          className={`border-b border-slate-700/50 hover:bg-gradient-to-r hover:from-blue-900/20 hover:to-purple-900/20 transition-all duration-200 ${
                            index % 2 === 0 
                              ? 'bg-gradient-to-r from-slate-800/40 to-slate-800/20' 
                              : 'bg-gradient-to-r from-slate-800/60 to-slate-800/30'
                          }`}
                        >
                        <td className="px-4 py-3 text-sm text-white/60 font-medium">{rowNumber}</td>
                        <td className="px-4 py-3 text-sm text-white font-medium">
                          {formatDateTime(transaction.transaction_date)}
                        </td>
                        <td className="px-4 py-3 text-sm text-white font-semibold">
                          {transaction.customer_name}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {isPS ? (
                            <div className="flex items-center gap-2">
                              <Gamepad2 className="h-4 w-4 text-blue-400 flex-shrink-0" />
                              <span className="text-white font-semibold text-xs whitespace-nowrap">
                                {transaction.ps_unit_name}
                              </span>
                            </div>
                          ) : (
                            <span className="text-white/40 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {isPS && transaction.rental_mode ? (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-bold w-fit ${
                              transaction.rental_mode === 'no_limit' 
                                ? 'bg-purple-500/20 text-purple-300 border border-purple-500/40'
                                : transaction.rental_mode === 'paket'
                                ? 'bg-green-500/20 text-green-300 border border-green-500/40'
                                : 'bg-blue-500/20 text-blue-300 border border-blue-500/40'
                            }`}>
                              {transaction.rental_mode === 'no_limit' ? ' NO LIMIT' : 
                               transaction.rental_mode === 'paket' ? ' PAKET' : 
                               ' DURASI'}
                            </span>
                          ) : (
                            <span className="text-white/40 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex flex-col gap-1">
                            {isPS ? (
                              <>
                                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-blue-500/20 text-blue-300 border border-blue-500/40 w-fit">
                                  <Gamepad2 className="h-3.5 w-3.5" />
                                  PS
                                </span>
                                {hasAdditionalItems && (
                                  <button
                                    onClick={() => toggleRowExpand(transaction.id)}
                                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-xs font-semibold bg-orange-500/20 text-orange-300 border border-orange-500/30 hover:bg-orange-500/30 transition-colors w-fit cursor-pointer"
                                  >
                                    <ShoppingCart className="h-3 w-3" />
                                    +{transaction.additional_items.length} F&B
                                    <span className="text-xs">{isExpanded ? '▼' : '▶'}</span>
                                  </button>
                                )}
                              </>
                            ) : (
                              <>
                                <button
                                  onClick={() => toggleRowExpand(transaction.id)}
                                  className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-orange-500/20 text-orange-300 border border-orange-500/40 hover:bg-orange-500/30 transition-colors w-fit cursor-pointer"
                                >
                                  <ShoppingCart className="h-3.5 w-3.5" />
                                  F&B ({transaction.items?.length || 0})
                                  <span className="text-xs">{isExpanded ? '▼' : '▶'}</span>
                                </button>
                              </>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          {isPaid ? (
                            <div className="flex items-center gap-1.5">
                              {transaction.payment_method === 'qris' ? (
                                <>
                                  <div className="w-6 h-6 bg-gradient-to-br from-purple-500 to-pink-500 rounded flex items-center justify-center flex-shrink-0">
                                    <span className="text-white text-[10px] font-bold">Q</span>
                                  </div>
                                  <span className="text-purple-300 font-semibold text-xs whitespace-nowrap">QRIS</span>
                                </>
                              ) : transaction.payment_method === 'cash' ? (
                                <>
                                  <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-emerald-500 rounded flex items-center justify-center flex-shrink-0">
                                    <span className="text-white text-xs font-bold"></span>
                                  </div>
                                  <span className="text-green-300 font-semibold text-xs whitespace-nowrap">Cash</span>
                                </>
                              ) : (
                                <span className="text-white/40 text-xs">-</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-white/40 text-xs">-</span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="flex items-center gap-1.5">
                            <div className="w-6 h-6 bg-slate-700/70 rounded-full flex items-center justify-center flex-shrink-0">
                              <span className="text-white text-xs"></span>
                            </div>
                            <span className="text-white/90 font-medium text-xs whitespace-nowrap">
                              {transaction.operator_name || '-'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-right">
                          <span className="text-white font-bold">
                            {formatCurrency(
                              isPS 
                                ? (transaction.final_price || transaction.total_price || 0)
                                : (transaction.total || 0)
                            )}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isPaid ? (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-green-500/20 text-green-300 border border-green-400/40">
                              <CheckCircle className="h-3.5 w-3.5" />
                              Lunas
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-bold bg-orange-500/20 text-orange-300 border border-orange-400/40">
                              <XCircle className="h-3.5 w-3.5" />
                              Belum
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {isPaid ? (
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                onPrintClick(transaction);
                              }}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-700 hover:to-blue-600 text-white text-xs font-bold rounded-lg transition-all shadow-lg hover:shadow-blue-500/50 hover:scale-105"
                              title="Cetak Struk"
                            >
                              <Printer className="h-3.5 w-3.5" />
                              Print
                            </button>
                          ) : (
                            isPS && (
                              <button
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handlePaymentClick(transaction);
                                }}
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-green-600 to-green-500 hover:from-green-700 hover:to-green-600 text-white text-xs font-bold rounded-lg transition-all shadow-lg hover:shadow-green-500/50 hover:scale-105"
                              >
                                 Bayar
                              </button>
                            )
                          )}
                        </td>
                      </tr>
                      
                      {/* Expanded Row - F&B Items Detail */}
                      {isExpanded && showExpandable && (
                        <tr className="bg-slate-900/60 border-b border-slate-700/50">
                          <td colSpan="10" className="px-4 py-3">
                            <div className="bg-gradient-to-br from-orange-900/30 to-orange-800/30 border border-orange-500/40 rounded-xl p-4 animate-in fade-in slide-in-from-top-2 duration-300">
                              <div className="flex items-center gap-2 mb-3">
                                <ShoppingCart className="h-4 w-4 text-orange-400" />
                                <h4 className="text-white font-bold text-sm">
                                  {isPS ? 'F&B Items (Pesanan Tambahan)' : 'Detail Pesanan F&B'}
                                </h4>
                              </div>
                              
                              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                                {(isPS ? transaction.additional_items : transaction.items)?.map((item, idx) => (
                                  <div 
                                    key={idx} 
                                    className="bg-slate-800/50 border border-slate-700/50 rounded-lg p-2.5 flex items-center justify-between"
                                  >
                                    <div className="flex-1">
                                      <p className="text-white font-semibold text-sm">{item.name}</p>
                                      <p className="text-orange-300 text-xs">
                                        Rp {item.price?.toLocaleString('id-ID')} × {item.qty || item.quantity}
                                      </p>
                                    </div>
                                    <div className="text-right">
                                      <p className="text-blue-300 font-bold text-sm">
                                        Rp {((item.price * (item.qty || item.quantity)) || 0).toLocaleString('id-ID')}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              
                              {/* Total F&B */}
                              <div className="mt-3 pt-3 border-t border-orange-500/30 flex justify-between items-center">
                                <span className="text-orange-300 font-semibold text-sm">Total F&B:</span>
                                <span className="text-orange-300 font-black text-base">
                                  Rp {(isPS 
                                    ? transaction.additional_items?.reduce((sum, item) => sum + (item.price * (item.qty || 1)), 0)
                                    : transaction.items?.reduce((sum, item) => sum + (item.price * (item.quantity || 1)), 0)
                                  ).toLocaleString('id-ID')}
                                </span>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-slate-900/30 border-t-2 border-slate-700 px-5 py-4">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-white/60 font-medium">
                    Menampilkan {startIndex + 1}-{Math.min(endIndex, transactions.length)} dari {transactions.length} transaksi
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={goToPrevPage}
                      disabled={currentPage === 1}
                      className="px-4 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-slate-700 to-slate-600 text-white hover:from-slate-600 hover:to-slate-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
                    >
                      ← Prev
                    </button>
                    <span className="text-white text-sm font-bold px-4 py-2 bg-slate-700/50 rounded-lg">
                      {currentPage} / {totalPages}
                    </span>
                    <button
                      onClick={goToNextPage}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 rounded-lg text-xs font-bold bg-gradient-to-r from-slate-700 to-slate-600 text-white hover:from-slate-600 hover:to-slate-500 transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg"
                    >
                      Next →
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Payment Dialog */}
      {showPaymentDialog && selectedRental && (
        <PaymentDialog
          open={showPaymentDialog}
          onClose={() => {
            setShowPaymentDialog(false);
            setSelectedRental(null);
          }}
          rental={selectedRental}
          onPaymentSuccess={handlePaymentSuccess}
        />
      )}

      {/* Print Dialogs */}
      <PrintDialog
        rental={selectedPrintRental}
        open={showPrintDialog}
        onClose={() => {
          setShowPrintDialog(false);
          setSelectedPrintRental(null);
        }}
      />

      <FnBPrintDialog
        order={selectedPrintFnB}
        open={showFnBPrintDialog}
        onClose={() => {
          setShowFnBPrintDialog(false);
          setSelectedPrintFnB(null);
        }}
      />
    </div>
  );
}
